// src/features/data/ingestion-pair-cell.tsx
//
// The Groups tab's PROD/DEV ingestion pair cell (IR-1697), moved out of
// groups-unified-tab.tsx unchanged so the Avatars tab's group drill-down can use
// the SAME cell (IR-1697 r10, QA finding 1) without the two tabs importing each
// other. groups-unified-tab re-exports both names.
import { StatusDot } from "@/components/iris";
import type { SortDirection } from "@/components/iris";
import type { StatusDotVariant } from "@/components/iris";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { fmtRelative, utcInstant } from "./avatars-tab.helpers";

/**
 * IR-1697 r15 (red team F2): the receipt time "2026-10-29 12:10:19 UTC (2h ago)"
 * with the exact instant as ONE non-breaking unit, so the date and the time never
 * split across lines in a narrow Ingestion cell. Only the "(2h ago)" may wrap.
 * Same text as `receiptTimeText`; an unparseable value keeps the relative age only.
 */
export function ReceiptTimeText({ iso, env }: { iso: string; env: "prod" | "dev" }) {
  const exact = utcInstant(iso);
  if (!exact) return <>{fmtRelative(iso)}</>;
  return (
    <>
      <span data-testid={`receipt-instant-${env}`} style={{ whiteSpace: "nowrap" }}>
        {exact}
      </span>
      {` (${fmtRelative(iso)})`}
    </>
  );
}

// IR-1726 r2: the values come from the backend's `resolve_group_ingestion` —
// one per-env rule for Rotem's "Ingestion meanings" matrix — and the served
// `*_label` is rendered verbatim beside the dot. Every value the backend can
// emit has an entry here: anything missing falls to PipelineStatusCell's
// `?? "idle"` grey, which would make a contradiction look like an absence.
const INGESTION_VARIANT: Record<string, StatusDotVariant> = {
  // Legacy — an older prod backend (before the r2 port) and the Avatars-tab
  // roll-up (IR-1697 `fold_ingestion`) still serve it. Kept so nothing regresses.
  ingested: "ok",
  // Data actually landed in this env — messages, the member list, or identity
  // only. All three are the "ingested" family and share the green.
  messages_ingested: "ok",
  roster_ingested: "ok",
  identity_ingested: "ok",
  never_ingested: "idle",
  // IR-1726 r2: absences or settled states — checked and nothing usable
  // landed, an old import, no current eligible payload. Neutral, same family
  // as `never_ingested`. Explicit here rather than left to the `?? "idle"`
  // fallback: the fallback exists for genuinely unmapped values, and a
  // settled state must never silently become "whatever idle means today" by
  // omission (same reasoning as MESSAGES_QUALITY_VARIANT's not_applicable).
  no_usable_payload: "idle",
  already_imported_historically: "idle",
  not_applicable: "idle",
  // IR-1697 r10: this deployment cannot read the environment. Neutral, not red:
  // it asserts nothing about the data, and it is never the "No data" grey word.
  could_not_check: "idle",
  // The contradiction: this env received items AFTER the stop/hold decision.
  // Attention, not failure — warn, not err — and never the same grey as
  // Not Applicable, which is what Rotem asked to see it apart from.
  ingested_after_stop: "warn",
  // Both failures are the attention state. They are told apart by their label
  // and their tooltip, not by hue: a reader scanning the column wants "this one
  // is broken" to look the same regardless of WHICH stage broke.
  group_ingestion_failed: "err",
  batch_ingestion_failed: "err",
};

/**
 * One pipeline-layer status: coloured dot + neutral label, matching
 * LifecycleBadge so Collection, Coverage and Ingestion read as one family.
 *
 * `value` null means UNKNOWN and renders an em dash with no dot — the same
 * evidence rule the Coverage column already followed in words: never draw a
 * state for something we have not measured.
 */
export function PipelineStatusCell({
  value,
  label,
  variants,
  title,
}: {
  value: string | null;
  label: string | null;
  variants: Record<string, StatusDotVariant>;
  // `| undefined` explicitly: this repo runs `exactOptionalPropertyTypes`, so
  // "may be omitted" and "may be passed as undefined" are different types, and
  // the Ingestion cell passes undefined when there is no dev-side detail.
  title?: string | undefined;
}) {
  if (!value && !label) return <span style={{ color: "var(--iris-fg-5)" }}>{"\u2014"}</span>;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }} title={title}>
      {value ? <StatusDot variant={variants[value] ?? "idle"} size={7} pulse={false} /> : null}
      <span style={{ color: "var(--iris-fg-2)" }}>{label ?? value}</span>
    </span>
  );
}

/**
 * IR-1697: Ingestion is per-environment BY NATURE (prod and dev hold
 * different data) — Yaniv's ruling on IR-1697 requires both shown together,
 * explicitly labelled, in the same visible-pair convention the Members and
 * Messages columns already use (`fmtPair`), rather than hiding DEV in a
 * tooltip the way the original IR-1228 P5 cell did. Two stacked
 * `PipelineStatusCell`s, each tagged PROD/DEV so the order is fixed and
 * named rather than positional.
 */
// IR-1697 r10 contract words. `could_not_check` has no served label, so the raw
// value must never reach the screen; `not_applicable` keeps a served label.
function ingestionCellProps(
  value: string | null,
  label: string | null,
  nullAsUnknown: boolean,
): { value: string | null; label: string | null } {
  if (value === "could_not_check") return { value, label: label ?? "Could Not Check" };
  if (value === "not_applicable") return { value, label: label ?? "Not Applicable" };
  if (value === null && label === null && nullAsUnknown) {
    return { value: "unknown", label: "Unknown" };
  }
  return { value, label: label ?? value };
}

export function IngestionPairCell({
  prodValue,
  prodLabel,
  devValue,
  devLabel,
  prodReceivedAt = null,
  prodReceiptLabel = null,
  devReceivedAt = null,
  devReceiptLabel = null,
  prodUnknownReason = null,
  devUnknownReason = null,
  nullAsUnknown = false,
}: {
  prodValue: string | null;
  prodLabel: string | null;
  devValue: string | null;
  devLabel: string | null;
  // IR-1697 r9 (F4): each environment's saved receipt, `<env>_last_ingested_at`
  // with the API's own word for it (`<env>_receipt_label`). The word is the
  // backend's; a time without it is not shown, exactly as on the Avatars line.
  prodReceivedAt?: string | null | undefined;
  prodReceiptLabel?: string | null | undefined;
  devReceivedAt?: string | null | undefined;
  devReceiptLabel?: string | null | undefined;
  /**
   * IR-1697 r35: the backend's own reason for an Unknown ingestion time
   * (`<env>_ingestion_time_unknown_reason`), printed verbatim under the environment
   * when no receipt time is shown. Never composed client-side.
   */
  prodUnknownReason?: string | null | undefined;
  devUnknownReason?: string | null | undefined;
  /**
   * IR-1697 r10: a null value with no label is an environment the screen has no
   * word for. The Groups tab keeps its em dash; the avatar drill-down passes true
   * so the same null reads Unknown, never a blank and never "No data".
   */
  nullAsUnknown?: boolean;
}) {
  const row = (
    env: "prod" | "dev",
    tag: string,
    value: string | null,
    label: string | null,
    at: string | null | undefined,
    word: string | null | undefined,
    reason: string | null | undefined,
  ) => (
    <span style={{ display: "inline-flex", flexDirection: "column", gap: 0 }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        <span style={{ color: "var(--iris-fg-5)", fontSize: 10 }}>{tag}</span>
        <PipelineStatusCell
          {...ingestionCellProps(value, label, nullAsUnknown)}
          variants={INGESTION_VARIANT}
        />
      </span>
      {at && word ? (
        <span
          className="iris-td--muted"
          style={{ fontSize: 10, paddingLeft: 26 }}
          data-testid={`group-receipt-${env}`}
          title={`${word} ${utcInstant(at)}`}
        >
          {`${word} `}
          <ReceiptTimeText iso={at} env={env} />
        </span>
      ) : reason ? (
        <span
          className="iris-td--muted"
          style={{ fontSize: 10, paddingLeft: 26 }}
          data-testid={`group-unknown-reason-${env}`}
          title={reason}
        >
          {reason}
        </span>
      ) : null}
    </span>
  );
  return (
    <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {row(
        "prod",
        "PROD",
        prodValue,
        prodLabel,
        prodReceivedAt,
        prodReceiptLabel,
        prodUnknownReason,
      )}
      {row("dev", "DEV", devValue, devLabel, devReceivedAt, devReceiptLabel, devUnknownReason)}
    </span>
  );
}

// IR-1697 r10: the Ingestion column header sorts by the headline. These two sort
// by PROD or by DEV, chosen explicitly. Same three-click cycle as SortableTh
// (ascending, descending, off); they live beside the filters because the header
// text is the column's name and must stay exactly that.
const INGESTION_ENV_WORDS = { prod: "Production", dev: "Development" } as const;

export function IngestionEnvSort({
  sortKey,
  sortDirection,
  onSort,
}: {
  sortKey: string | null;
  sortDirection: SortDirection;
  onSort: (key: string, direction: SortDirection) => void;
}) {
  return (
    <span
      data-testid="ingestion-env-sort"
      style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12 }}
    >
      <span style={{ color: "var(--iris-fg-4)" }}>Sort ingestion by</span>
      {(["prod", "dev"] as const).map((env) => {
        const key = `ingestion_${env}`;
        const active = sortKey === key;
        const Icon = !active ? ArrowUpDown : sortDirection === "asc" ? ArrowUp : ArrowDown;
        const label = INGESTION_ENV_WORDS[env];
        return (
          <button
            key={env}
            type="button"
            aria-label={`Sort by ${label} ingestion`}
            aria-pressed={active}
            onClick={() => {
              const next: SortDirection = !active ? "asc" : sortDirection === "asc" ? "desc" : null;
              onSort(key, next);
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 2,
              font: "inherit",
              color: "inherit",
              background: "transparent",
              border: "1px solid var(--iris-line-2)",
              borderRadius: 6,
              padding: "1px 6px",
              cursor: "pointer",
            }}
          >
            {label}
            <Icon
              size={12}
              style={{ color: active ? "var(--iris-neon-cyan)" : "var(--iris-fg-5)" }}
            />
          </button>
        );
      })}
    </span>
  );
}
